import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Send, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const newItem = () => ({
  title: "",
  console: "",
  components: "Game in Box with Manual",
  condition: "Like New",
});

export default function QuotePage() {
  const navigate = useNavigate();
  const [consoles, setConsoles] = useState([]);
  const [options, setOptions] = useState({ conditions: [], components: [] });

  const [customer, setCustomer] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
  });
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [quoteType, setQuoteType] = useState("cash"); // cash | trade
  const [items, setItems] = useState([newItem()]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/consoles").then((r) => setConsoles(r.data));
    api.get("/options").then((r) => setOptions(r.data));
  }, []);

  const updateItem = (idx, key, val) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };

  const addItem = () => setItems((p) => [...p, newItem()]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!first.trim() || !last.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (items.some((it) => !it.title.trim() || !it.console)) {
      setError("Every game line needs a title and a console.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/quotes", {
        ...customer,
        customer_name: `${first.trim()} ${last.trim()}`,
        quote_type: quoteType,
        items,
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const label = result.quote_type === "cash" ? "Cash Out Value" : "Trade-In Credit";
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Quote Submitted</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mb-2">Thanks, {result.customer_name.split(" ")[0]}.</h1>
          <p className="text-sm mb-10" style={{ color: "var(--ab-text-muted)" }}>
            We've received your {result.quote_type.toUpperCase()} quote. The owner has been notified and will review
            in store. A copy of these initial values has been sent to {result.customer_email}.
          </p>

          <div className="surface" data-testid="quote-result-card">
            <div className="px-5 py-4 border-b flex justify-between items-center" style={{ borderColor: "var(--ab-border)" }}>
              <span className="label-eyebrow">Your Items</span>
              <span className="label-eyebrow">{label}</span>
            </div>
            {result.items.map((it) => (
              <div key={it.id} className="px-5 py-4 border-b flex justify-between items-start gap-4" style={{ borderColor: "var(--ab-border)" }} data-testid="quote-result-item">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-base text-white truncate">{it.title}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--ab-text-dim)" }}>
                    {it.console} &middot; {it.components} &middot; {it.condition}
                  </div>
                </div>
                <div className="text-right">
                  {it.value != null ? (
                    <div className="text-xl font-bold" style={{ color: "var(--ab-primary)" }}>
                      ${it.value.toFixed(2)}
                    </div>
                  ) : (
                    <div className="text-xs px-2 py-1" style={{ background: "var(--ab-warning)", color: "var(--ab-bg)" }}>
                      Pending review
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="px-5 py-5 flex justify-between items-center" style={{ background: "var(--ab-surface-2)" }}>
              <span className="label-eyebrow text-white">Total {label}</span>
              <span className="text-2xl font-bold" style={{ color: "var(--ab-primary)" }} data-testid="quote-result-total">
                ${result.total.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            <button className="btn-primary" onClick={() => navigate("/")} data-testid="quote-result-home-btn">Return Home</button>
            <button className="btn-secondary" onClick={() => { setResult(null); setItems([newItem()]); }} data-testid="quote-result-new-btn">Start a New Quote</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="label-eyebrow mb-3">Step 01</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-3">Trade-In / Cash Out Quote</h1>
        <p className="text-sm max-w-2xl" style={{ color: "var(--ab-text-muted)" }}>
          Enter your contact info, then add every game you'd like to sell or trade. We'll pull live sales data
          to generate your initial quote.
        </p>

        <form onSubmit={submit} className="mt-12">
          {/* Customer */}
          <div className="surface p-6 mb-6">
            <div className="label-eyebrow mb-5" style={{ color: "var(--ab-primary)" }}>Your Info</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-eyebrow block mb-2">First Name</label>
                <input className="input-terminal" value={first} onChange={(e) => setFirst(e.target.value)} required data-testid="quote-first-name" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Last Name</label>
                <input className="input-terminal" value={last} onChange={(e) => setLast(e.target.value)} required data-testid="quote-last-name" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">E-Transfer Email</label>
                <input
                  className="input-terminal"
                  type="email"
                  value={customer.customer_email}
                  onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })}
                  required
                  data-testid="quote-email"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Phone</label>
                <input
                  className="input-terminal"
                  value={customer.customer_phone}
                  onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })}
                  required
                  data-testid="quote-phone"
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="surface p-6 mb-6">
            <div className="flex justify-between items-center mb-5">
              <div className="label-eyebrow" style={{ color: "var(--ab-primary)" }}>Games</div>
              <button type="button" onClick={addItem} className="btn-secondary inline-flex items-center gap-2" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="quote-add-item-btn">
                <Plus size={14} /> Add Game
              </button>
            </div>

            <div className="space-y-4">
              {items.map((it, idx) => (
                <div key={idx} className="border p-4 grid sm:grid-cols-12 gap-3 items-end" style={{ borderColor: "var(--ab-border)", background: "var(--ab-bg)" }} data-testid={`quote-item-row-${idx}`}>
                  <div className="sm:col-span-4">
                    <label className="label-eyebrow block mb-2">Game Title</label>
                    <input className="input-terminal" value={it.title} onChange={(e) => updateItem(idx, "title", e.target.value)} required data-testid={`quote-item-title-${idx}`} />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="label-eyebrow block mb-2">Console</label>
                    <select className="input-terminal" value={it.console} onChange={(e) => updateItem(idx, "console", e.target.value)} required data-testid={`quote-item-console-${idx}`}>
                      <option value="">Select…</option>
                      {consoles.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-3">
                    <label className="label-eyebrow block mb-2">Components</label>
                    <select className="input-terminal" value={it.components} onChange={(e) => updateItem(idx, "components", e.target.value)} data-testid={`quote-item-components-${idx}`}>
                      {options.components.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-eyebrow block mb-2">Condition</label>
                    <select className="input-terminal" value={it.condition} onChange={(e) => updateItem(idx, "condition", e.target.value)} data-testid={`quote-item-condition-${idx}`}>
                      {options.conditions.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-12 flex justify-end">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(idx)} className="text-xs inline-flex items-center gap-1 label-eyebrow hover:text-[var(--ab-danger)] transition-colors" data-testid={`quote-item-remove-${idx}`}>
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Toggle + submit */}
          <div className="surface p-6 mb-6">
            <div className="label-eyebrow mb-4" style={{ color: "var(--ab-primary)" }}>Compensation Method</div>
            <div className="inline-flex border" style={{ borderColor: "var(--ab-border)" }} role="radiogroup">
              <button
                type="button"
                onClick={() => setQuoteType("cash")}
                className="px-6 py-3 text-xs uppercase tracking-widest font-bold"
                style={{
                  background: quoteType === "cash" ? "var(--ab-primary)" : "transparent",
                  color: quoteType === "cash" ? "var(--ab-bg)" : "var(--ab-text)",
                }}
                data-testid="quote-toggle-cash"
              >Cash</button>
              <button
                type="button"
                onClick={() => setQuoteType("trade")}
                className="px-6 py-3 text-xs uppercase tracking-widest font-bold border-l"
                style={{
                  background: quoteType === "trade" ? "var(--ab-primary)" : "transparent",
                  color: quoteType === "trade" ? "var(--ab-bg)" : "var(--ab-text)",
                  borderColor: "var(--ab-border)",
                }}
                data-testid="quote-toggle-trade"
              >Trade Credit</button>
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--ab-text-dim)" }}>
              Trade credit yields up to 10% more value per item than cash.
            </p>
          </div>

          {error && <div className="mb-4 px-4 py-3 text-sm" style={{ background: "rgba(239,68,68,0.1)", color: "var(--ab-danger)", border: "1px solid var(--ab-danger)" }} data-testid="quote-error">{error}</div>}

          <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2" data-testid="quote-submit-btn">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit Quote
          </button>
        </form>
      </div>
    </Layout>
  );
}
